import type { ClassificationAnswers } from './classification';
import type { Role } from './roles';

export const REGULATORY_OUTCOMES = [
  'applies',
  'not_applicable',
  'uncertain',
  'escalated',
] as const;
export type RegulatoryOutcome = (typeof REGULATORY_OUTCOMES)[number];

export const OUTCOME_LABELS: Readonly<Record<RegulatoryOutcome, string>> = {
  applies: 'Applies',
  not_applicable: 'Not applicable',
  uncertain: 'Uncertain',
  escalated: 'Escalated to Compliance',
};

export type Regime = {
  id: string;
  name: string;
  defaultOwner: Role;
  suggest: (answers: ClassificationAnswers) => {
    outcome: RegulatoryOutcome;
    note: string;
  };
};

const notApplicable = (note = '') => ({
  outcome: 'not_applicable' as const,
  note,
});

/** S11 regimes (journey spec §4.2); deadlines are not encoded (ASDP-REG-05). */
export const REGIMES: readonly Regime[] = [
  {
    id: 'gdpr',
    name: 'GDPR',
    defaultOwner: 'dpo',
    suggest: a =>
      a.pii && a.pii !== 'none'
        ? { outcome: 'applies', note: 'DPIA to be approved before go-live' }
        : notApplicable('No personal data'),
  },
  {
    id: 'dora',
    name: 'DORA',
    defaultOwner: 'security_champion',
    suggest: a =>
      a.reg === 'sector' || a.reg === 'critical'
        ? { outcome: 'applies', note: 'Financial-sector ICT risk controls' }
        : notApplicable('No sector regulation'),
  },
  {
    id: 'nis2',
    name: 'NIS2 / Cybersecurity Act',
    defaultOwner: 'security_champion',
    suggest: a =>
      a.reg === 'critical'
        ? { outcome: 'applies', note: 'The function itself is regulated' }
        : { outcome: 'uncertain', note: 'Confirm with Compliance' },
  },
  {
    id: 'ai_act',
    name: 'EU AI Act',
    defaultOwner: 'product_owner',
    suggest: a =>
      a.aisys === 'yes'
        ? {
            outcome: 'uncertain',
            note: 'Classify the system: prohibited, high-risk or Art. 50',
          }
        : notApplicable("The application doesn't use AI"),
  },
  {
    id: 'eaa',
    name: 'European Accessibility Act',
    defaultOwner: 'product_owner',
    suggest: a =>
      a.crit === 'customers' || a.crit === 'critical'
        ? { outcome: 'applies', note: 'Customer-facing service' }
        : notApplicable('Internal users only'),
  },
  {
    id: 'cra',
    name: 'Cyber Resilience Act',
    defaultOwner: 'security_champion',
    suggest: () => notApplicable('Delivered as a service'),
  },
  {
    id: 'product_liability',
    name: 'Product Liability Directive',
    defaultOwner: 'product_owner',
    suggest: () => notApplicable('Delivered as a service, not placed on the market as a product'),
  },
  {
    id: 'sector_contract',
    name: 'Sector and contract rules',
    defaultOwner: 'product_owner',
    suggest: a =>
      a.reg === 'sector' || a.reg === 'critical'
        ? { outcome: 'applies', note: 'Record the sector and contract overlay in the security plan' }
        : notApplicable('No sector regulation; the contract adds no security requirements'),
  },
];

export type RegulatoryRow = {
  regime: string;
  outcome?: RegulatoryOutcome;
  /** GitHub login. */
  owner?: string;
  note?: string;
  /** Link to the Compliance issue once escalated. */
  escalation?: string;
};

export const suggestRegulatoryRows = (
  answers: ClassificationAnswers,
): RegulatoryRow[] =>
  REGIMES.map(regime => ({ regime: regime.id, ...regime.suggest(answers) }));

/** What a row still needs; empty when complete. */
export function regulatoryRowProblem(row: RegulatoryRow): string {
  const name = REGIMES.find(r => r.id === row.regime)?.name ?? row.regime;
  if (!row.outcome) return `${name}: choose an outcome`;
  if (!row.owner) return `${name}: name an owner`;
  if (row.outcome === 'not_applicable' && !row.note?.trim()) {
    return `${name}: say why it doesn't apply`;
  }
  if (row.outcome === 'uncertain') return `${name}: escalate to Compliance`;
  if (row.outcome === 'escalated' && !row.escalation) {
    return `${name}: the escalation has no issue link`;
  }
  return '';
}
